import * as React from "react";
import * as ReactDOM from "react-dom";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../mobx/components";
import { Button, RotateLoader } from "@lib/components";
import moment, { Moment } from "moment-timezone";
import { withTranslation, WithTranslation } from "react-i18next";
import { FaBan, FaBookOpen, FaCalendarAlt, FaChevronDown, FaChevronLeft, FaChevronRight, FaChevronUp, FaDownload, FaPrint, FaRegClock, FaSearch, FaTh, FaUser } from "react-icons/fa";
import {
	dineInTableCapacity,
	FORMATS,
	getOpenWindowIntervalsMsForDay,
	getTableBookingSlotsForWeekday,
	isBookingIntervalWithinOpenHours,
	Untrusive,
	logger,
} from "@lib/common";
import { nanoid } from "nanoid";
import QRCode from "qrcode.react";
import ReactDOMServer from "react-dom/server";
import { UI } from "../../../../../core/ui";
import { BookingCustomerSearch } from "./booking-customer-search";
import { BookingNotesIndicator } from "./booking-notes-indicator";
import {
	BOOKING_LANE_HEIGHT_PX,
	BookingJoinRowRole,
	DEFAULT_BLOCK_DURATION_MINS,
	MIN_TABLE_ROW_PX,
	TABLE_COL_WIDTH_NARROW_PX,
	TABLE_COL_WIDTH_WIDE_PX,
	assignLanes,
	bookingJoinRowRole,
	buildClosedTimelineSegmentsPx,
	canTableRowFitParty,
	computePlacementHighlight,
	computeTimelineScrollLeft,
	csvCell,
	downloadCsv,
	formatBookingTableLabel,
	liveTableDocketTableHeadline,
	getTableBookingSlotIntervalMinutes,
	getTimelineBounds,
	isCalendarPlacementSlotValid,
	isTableSlotAvailable,
	resolveCalendarPlacementTableIds,
	tableBlockTimelinePx,
} from "./calendar.helpers";
import {
	BlockMeta,
	BlockMetaItem,
	BlockTableLabel,
	BlockTitle,
	BookingBlockBtn,
	BookingBlockOuter,
	CalendarFilterBar,
	CalendarFilterField,
	CalendarPageHeader,
	CalendarShell,
	CalendarSubtitle,
	CalendarTitle,
	CalendarToolbar,
	ClosedOverlayStrip,
	TableBlockStrip,
	CornerCell,
	CreateTimelineSlot,
	DatePickerBackdrop,
	DatePickerCard,
	DatePickerDayBtn,
	DatePickerGrid,
	DatePickerHeader,
	DatePickerMonthTitle,
	DatePickerNavBtn,
	DatePickerWeekday,
	DatePickerWeekdays,
	DocketActions,
	DocketBackdrop,
	DocketCard,
	DocketCloseBtn,
	DocketEyebrow,
	DocketEyebrowText,
	DocketGuestBlock,
	DocketGuestLabel,
	DocketGuestName,
	DocketHintBox,
	DocketHintText,
	DocketParty,
	DocketTableBand,
	DocketTableTitle,
	DocketVenue,
	GridRow,
	GridWrap,
	HourCell,
	HourTrack,
	LegendRow,
	LegendSwatch,
	LinkCustomerPopover,
	NotesPopover,
	NowLine,
	PartyBadge,
	PlacementSlot,
	QueueAssignSeatBtn,
	QueueBadge,
	QueueCardRoot,
	QueueCardTop,
	QueueConfirmActions,
	QueueConfirmBtn,
	QueueContentBody,
	QueueEmptyIcon,
	QueueEmptyState,
	QueueEmptySubtext,
	QueueEmptyTitle,
	QueueFilterHeader,
	QueueFilterSelect,
	QueueTableSearchInput,
	QueueTableSearchWrap,
	QueueFooter,
	QueueGuest,
	QueueHeader,
	QueueLegendFooter,
	QueueList,
	QueueMetaRow,
	QueueParty,
	QueuePanel,
	QueuePendingTitle,
	QueuePlaceHint,
	QueueTime,
	QuickActionDocket,
	QuickActionGhost,
	QuickActionPrimary,
	QuickActionsBar,
	RowLabel,
	RowLabelMeta,
	RowLabelTitle,
	ScrollArea,
	StatusMenuItem,
	StatusMenuList,
	StatusMenuWrap,
	StickyHeader,
	TimeAdjustBackdrop,
	ResModalCard,
	ResModalHeader,
	ResModalFeaturedIcon,
	ResModalTitleCol,
	ResModalTitle,
	ResModalDesc,
	ResModalNumberBadge,
	ResModalCloseBtn,
	ResModalBody,
	ResModalActionBtn,
	ResModalInfoRow,
	ResModalInfoBlock,
	ResModalInfoTitle,
	ResModalMetaRow,
	ResModalMetaLabel,
	ResModalMetaValue,
	TimelineTrack,
	ToolbarDownloadBtn,
	ToolbarIconBtn,
	ToolbarPill,
	ToolbarTodayBtn,
	TurnPill,
	TurnPillInput,
	TurnPillLabel,
} from "./calendar.styles";

interface Props extends WithTranslation {
	/** Container element to portal the calendar toolbar into (inline with parent tabs). */
	toolbarContainer?: HTMLElement | null;
	/** Increment to force reload (e.g. after creating a booking from parent). */
	reloadSignal?: number;
	/** Live booking collection maintained by Reservations / notification flow. */
	liveBookings?: T.Schema.Booking.BookingSchema[];
	liveBookingsKey?: string;
	/** Parent opens create booking modal with date/time from an empty slot click. */
	onRequestCreateAtSlot?: (slot: { date: string; time: string; table_id?: string }) => void;
	/** Hide selected booking chrome while another dashboard modal is open. */
	suppressSelection?: boolean;
}

interface State {
	loading: boolean;
	items: T.Schema.Booking.BookingSchema[];
	day: Moment;
	selectedId: string | null;
	turnMinutes: number;
	editingNotesId: string | null;
	notesDraft: string;
	/** Cloudtable-style: pick a table timeline slot to place this booking. */
	movingBookingId: string | null;
	/** Pending-queue inline confirm panel for unconfirmed bookings. */
	queueConfirmId: string | null;
	/** Expanded queue card (chevron-up state). */
	queueExpandId: string | null;
	docketBooking: T.Schema.Booking.BookingSchema | null;
	/** Placing at a different time than the booking’s requested time — confirm notify guest. */
	adjustmentPending: {
		bookingId: string;
		newTs: number;
		tableIds: string[];
	} | null;
	/** Inline CRM link picker for a booking chip. */
	linkingCustomerBookingId: string | null;
	datePickerOpen: boolean;
	datePickerMonth: Moment;
	/** Visible calendar width used to scale hour columns on wide screens. */
	gridWidth: number;
	tableSearch: string;
	tableFilter: string;
	statusMenuId: string | null;
	dragOverKey: string | null;
	/** Toggle to add table holds on the timeline (not guest bookings). */
	blockTableMode: boolean;
}

/**
 * Floor-style day calendar inspired by cloudtable `CalendarView`: pending queue + scrollable timeline
 * with table rows from Dine In. Booking blocks are read-only; click opens the existing detail modal route.
 */
@inject("store")
@observer
class BookingsCalendarClass extends MobxComponent<Props, State> {
	private scrollAreaEl: HTMLDivElement | null = null;
	private dragGhostEl: HTMLElement | null = null;
	private dragOccurred = false;
	private draggingId: string | null = null;
	private panPointerId: number | null = null;
	private panStartX = 0;
	private panScrollLeft = 0;
	private panMoved = false;
	/** Day key (YYYY-MM-DD) pending initial horizontal scroll after load. */
	private timelineScrollDayKey: string | null = null;

	constructor(props: Props) {
		super(props);
		this.state = {
			loading: true,
			items: [],
			day: moment(),
			selectedId: null,
			turnMinutes: DEFAULT_BLOCK_DURATION_MINS,
			editingNotesId: null,
			notesDraft: "",
			movingBookingId: null,
			queueConfirmId: null,
			queueExpandId: null,
			docketBooking: null,
			adjustmentPending: null,
			linkingCustomerBookingId: null,
			datePickerOpen: false,
			datePickerMonth: moment(),
			gridWidth: 0,
			tableSearch: "",
			tableFilter: "",
			statusMenuId: null,
			dragOverKey: null,
			blockTableMode: false,
		};
	}

	getTableBlocks = (): T.Schema.Restaurant.Services.TableBookingBlock[] =>
		this.injected.store.restaurant?.settings.services?.table_booking
			?.table_blocks || [];

	persistTableBlocks = async (
		blocks: T.Schema.Restaurant.Services.TableBookingBlock[]
	) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		try {
			const res = await store.api.restaurant_update({
				_id: r._id,
				update: {
					$set: { "settings.services.table_booking.table_blocks": blocks },
				},
			});
			if (res.outcome !== 0) {
				throw new Error((res as { message?: string }).message || "save_failed");
			}
			r.settings.services.table_booking.table_blocks = blocks;
			this.forceUpdate();
			return true;
		} catch {
			UI.notification.error("Could not save table blocks");
			return false;
		}
	};

	toggleBlockTableMode = () => {
		this.draggingId = null;
		this.setState((s) => ({
			blockTableMode: !s.blockTableMode,
			movingBookingId: null,
			selectedId: null,
			queueConfirmId: null,
			adjustmentPending: null,
			statusMenuId: null,
			dragOverKey: null,
		}));
	};

	openFloorPlanSettings = () => {
		const { store } = this.injected;
		const r = store.restaurant;
		if (!r) return;
		store.router.push(
			`/restaurant/${r._id}/settings?redirect=Services&service=dine_in&tab=floor_plan`
		);
	};

	addTableBlock = async (
		tableId: string,
		slotMinsRaw: number,
		ctx: {
			day: Moment;
			tz: string;
			startHour: number;
		}
	) => {
		const { turnMinutes } = this.state;
		const slotIv = getTableBookingSlotIntervalMinutes(this.injected.store.restaurant!);
		const snapped = Math.round(slotMinsRaw / slotIv) * slotIv;
		const dur = turnMinutes;
		const rLocal = this.injected.store.restaurant!;
		const weekdayForHours = ctx.day
			.clone()
			.tz(ctx.tz)
			.locale("en")
			.format("dddd") as T.DaysOfWeek;
		const slotsForPlacement = getTableBookingSlotsForWeekday(rLocal, weekdayForHours);
		const winMs = getOpenWindowIntervalsMsForDay(ctx.day.clone(), ctx.tz, slotsForPlacement);
		const dayStartPl = ctx.day.clone().tz(ctx.tz).startOf("day");
		const absStartMin = ctx.startHour * 60 + snapped;
		const start = dayStartPl.clone().add(absStartMin, "minutes");
		const tsStartPl = start.valueOf();
		const tsEndPl = tsStartPl + dur * 60_000;
		if (!isBookingIntervalWithinOpenHours(winMs, tsStartPl, tsEndPl)) {
			UI.notification.error(
				this.injected.t("Outside service hours", {
					defaultValue: "Outside service hours",
				})
			);
			return;
		}
		if (
			!isTableSlotAvailable(
				tableId,
				snapped,
				dur,
				this.state.items,
				undefined,
				ctx.startHour,
				ctx.tz,
				(b) => this.durationMinsForBooking(b, turnMinutes),
				{ day: ctx.day, tableBlocks: this.getTableBlocks() }
			)
		) {
			UI.notification.error(
				this.injected.t("Table not free for this window", {
					defaultValue: "Table not free for this window",
				})
			);
			return;
		}
		const date = start.format(FORMATS.moment.date);
		const block: T.Schema.Restaurant.Services.TableBookingBlock = {
			_id: `TBL${nanoid()}`,
			table_ids: [tableId],
			date,
			start: start.format(FORMATS.moment.time),
			end: start.clone().add(dur, "minutes").format(FORMATS.moment.time),
		};
		const ok = await this.persistTableBlocks([...this.getTableBlocks(), block]);
		if (ok) {
			UI.notification.success(
				this.injected.t("Table blocked", { defaultValue: "Table blocked" })
			);
		}
	};

	removeTableBlock = async (blockId: string) => {
		const { t } = this.injected;
		if (
			!window.confirm(
				t("Remove this table block?", {
					defaultValue: "Remove this table block?",
				})
			)
		) {
			return;
		}
		const next = this.getTableBlocks().filter((b) => b._id !== blockId);
		const ok = await this.persistTableBlocks(next);
		if (ok) {
			UI.notification.success(
				t("Block removed", { defaultValue: "Block removed" })
			);
		}
	};

	componentDidMount() {
		const r = this.injected.store.restaurant!;
		const def = r.settings.services?.table_booking?.booking_logic?.default_turn_minutes;
		if (def && def > 0) {
			this.setState({ turnMinutes: def });
		}
		window.addEventListener("resize", this.updateGridWidth);
		window.addEventListener("keydown", this.handleCalendarKeyDown);
		this.loadDay(this.state.day);
		this.updateGridWidth();
	}

	componentDidUpdate(prevProps: Props) {
		if (
			prevProps.reloadSignal !== this.props.reloadSignal &&
			this.props.reloadSignal !== undefined
		) {
			this.loadDay(this.state.day);
		}
		if (prevProps.liveBookingsKey !== this.props.liveBookingsKey) {
			this.syncLiveBookingsToVisibleDay();
		}
		if (this.props.suppressSelection && !prevProps.suppressSelection) {
			this.setState({
				selectedId: null,
				statusMenuId: null,
				editingNotesId: null,
				linkingCustomerBookingId: null,
				movingBookingId: null,
				queueConfirmId: null,
			});
		}
		this.updateGridWidth();
	}

	componentWillUnmount() {
		window.removeEventListener("resize", this.updateGridWidth);
		window.removeEventListener("keydown", this.handleCalendarKeyDown);
		this.clearDragGhost();
	}

	setScrollAreaRef = (el: HTMLDivElement | null) => {
		this.scrollAreaEl = el;
		this.updateGridWidth();
	};

	openDatePicker = () => {
		this.setState((s) => ({
			datePickerOpen: !s.datePickerOpen,
			datePickerMonth: s.day.clone(),
		}));
	};

	closeDatePicker = () => {
		this.setState({ datePickerOpen: false });
	};

	moveDatePickerMonth = (delta: number) => {
		this.setState((s) => ({
			datePickerMonth: s.datePickerMonth.clone().add(delta, "month"),
		}));
	};

	selectDatePickerDay = (date: Moment) => {
		this.loadDay(date.clone());
	};

	updateGridWidth = () => {
		const next = this.scrollAreaEl?.clientWidth || 0;
		if (next && Math.abs(next - this.state.gridWidth) > 1) {
			this.setState({ gridWidth: next }, () => {
				if (this.timelineScrollDayKey) {
					this.applyTimelineScroll();
				}
			});
		} else if (this.timelineScrollDayKey) {
			this.applyTimelineScroll();
		}
	};

	scheduleTimelineScroll = (dayKey: string) => {
		this.timelineScrollDayKey = dayKey;
		requestAnimationFrame(() => {
			requestAnimationFrame(() => this.applyTimelineScroll());
		});
	};

	applyTimelineScroll = () => {
		if (!this.timelineScrollDayKey || !this.scrollAreaEl) return;
		const { store } = this.injected;
		const r = store.restaurant!;
		const tz = r.settings.region.timezone;
		const { day, items, gridWidth } = this.state;
		const dayKey = day.clone().tz(tz).format(FORMATS.moment.date);
		if (dayKey !== this.timelineScrollDayKey) return;

		const narrow = store.view.screen_width < 768;
		const tableColW = narrow ? TABLE_COL_WIDTH_NARROW_PX : TABLE_COL_WIDTH_WIDE_PX;
		const { startHour, endHour } = getTimelineBounds(r, day, tz);
		const numHours = Math.max(1, endHour - startHour);
		const baseSlotSize = narrow ? 80 : 120;
		const viewportWidth = this.scrollAreaEl.clientWidth || gridWidth;
		if (!viewportWidth) return;
		const visibleTimelineWidth = Math.max(0, viewportWidth - tableColW);
		const minTimeline = numHours * baseSlotSize;
		const slotSize =
			visibleTimelineWidth > minTimeline
				? Math.floor(visibleTimelineWidth / numHours)
				: baseSlotSize;
		const minuteSize = slotSize / 60;
		const isToday = day.clone().tz(tz).isSame(moment().tz(tz), "day");

		this.scrollAreaEl.scrollLeft = computeTimelineScrollLeft({
			startHour,
			endHour,
			minuteSize,
			tableColW,
			viewportWidth,
			day,
			tz,
			items,
			scrollToNowWhenEmpty: isToday,
		});
		this.timelineScrollDayKey = null;
	};

	syncLiveBookingsToVisibleDay = () => {
		const liveBookings = this.props.liveBookings || [];
		if (!liveBookings.length) return;

		const r = this.injected.store.restaurant!;
		const tz = r.settings.region.timezone;
		const start = this.state.day.clone().tz(tz).startOf("day").valueOf();
		const end = this.state.day.clone().tz(tz).endOf("day").valueOf();
		const visibleLiveBookings = liveBookings.filter(
			(b) => b.config.timestamp >= start && b.config.timestamp <= end
		);
		if (!visibleLiveBookings.length) return;

		this.setState((s) => {
			const existingIds = new Set(s.items.map((b) => b._id));
			const additions = visibleLiveBookings.filter((b) => !existingIds.has(b._id));
			if (!additions.length) return null;
			return {
				items: [...s.items, ...additions].sort(
					(a, b) => a.config.timestamp - b.config.timestamp
				),
			};
		});
	};

	openBooking = (row: T.Schema.Booking.BookingSchema) => {
		const { store } = this.injected;
		const rid = store.restaurant!._id;
		store.router.push(`/restaurant/${rid}/bookings?_id=${row._id}`);
		store.notifications.mark_read_object("booking", row._id);
		this.setState({
			selectedId: row._id,
			movingBookingId: null,
			queueConfirmId: null,
			editingNotesId: null,
			linkingCustomerBookingId: null,
		});
	};

	/** Matches cloudtable `handleUnallocatedClick`: unconfirmed → inline confirm; else → placement mode. */
	handleQueueCardClick = (b: T.Schema.Booking.BookingSchema, ev: React.MouseEvent) => {
		ev.stopPropagation();
		const { queueConfirmId, movingBookingId } = this.state;
		if (b.status === "unconfirmed") {
			this.setState({
				queueConfirmId: queueConfirmId === b._id ? null : b._id,
				movingBookingId: null,
				selectedId: null,
				editingNotesId: null,
				linkingCustomerBookingId: null,
			});
		} else {
			this.setState({
				movingBookingId: movingBookingId === b._id ? null : b._id,
				queueConfirmId: null,
				selectedId: null,
				editingNotesId: null,
				linkingCustomerBookingId: null,
			});
		}
	};

	confirmQueueBooking = async (
		b: T.Schema.Booking.BookingSchema,
		opts: { silent: boolean },
		ev: React.MouseEvent
	) => {
		ev.stopPropagation();
		const { store } = this.injected;
		try {
			const res = await store.api.booking_update_status({
				_id: b._id,
				status: "confirmed",
				skip_customer_email: opts.silent,
			});
			if (res.outcome !== 0) {
				throw new Error((res as { message?: string }).message || "confirm_failed");
			}
			this.setState((s) => ({
				items: s.items.map((x) => (x._id === b._id ? res.booking : x)),
				queueConfirmId: null,
			}));
			store.updateBookingComplete(res.booking);
			UI.notification.success(
				opts.silent
					? "Booking confirmed (no email)"
					: "Booking confirmed"
			);
		} catch {
			UI.notification.error("Could not confirm booking");
		}
	};

	toggleBookingSelect = (row: T.Schema.Booking.BookingSchema, ev: React.MouseEvent) => {
		ev.stopPropagation();
		const { selectedId } = this.state;
		if (selectedId === row._id) {
			this.setState({
				selectedId: null,
				movingBookingId: null,
				queueConfirmId: null,
				editingNotesId: null,
				linkingCustomerBookingId: null,
				notesDraft: "",
				statusMenuId: null,
			});
		} else {
			this.setState({
				selectedId: row._id,
				movingBookingId: null,
				queueConfirmId: null,
				editingNotesId: null,
				linkingCustomerBookingId: null,
				notesDraft: row.notes || "",
			});
		}
	};

	durationMinsForBooking = (
		b: T.Schema.Booking.BookingSchema,
		fallbackTurn: number
	) =>
		b.table_turn_minutes && b.table_turn_minutes > 0 ? b.table_turn_minutes : fallbackTurn;

	downloadDayBookings = () => {
		const { store, t } = this.injected;
		const r = store.restaurant!;
		const tz = r.settings.region.timezone;
		const tables: T.Schema.Restaurant.Services.DineInTable[] =
			r.settings.services?.dine_in?.tables || [];
		const tableById = new Map(tables.map((tbl) => [tbl._id, tbl]));
		const bookings = this.state.items
			.filter((b) => b.status !== "cancelled")
			.sort((a, b) => a.config.timestamp - b.config.timestamp);

		if (!bookings.length) {
			UI.notification.error(
				t("No bookings to download for this day.", {
					defaultValue: "No bookings to download for this day.",
				})
			);
			return;
		}

		const tableLabel = (b: T.Schema.Booking.BookingSchema) => {
			if (!b.table_ids?.length) {
				return t("Unassigned", { defaultValue: "Unassigned" });
			}
			return b.table_ids
				.map((id) => tableById.get(id)?.name?.trim() || id)
				.join(", ");
		};
		const dayStamp = this.state.day.clone().tz(tz).format("YYYY-MM-DD");
		const restaurantSlug =
			r.name
				.toLowerCase()
				.replace(/[^a-z0-9]+/gi, "-")
				.replace(/^-+|-+$/g, "") || "restaurant";
		const rows = [
			[
				"Time",
				"Guest",
				"Phone",
				"Email",
				"Guests",
				"Status",
				"Table",
				"Booking #",
				"Notes",
			],
			...bookings.map((b) => [
				moment(b.config.timestamp).tz(tz).locale(store.intl.s.lng).format("LT"),
				b.customer.name,
				b.customer.phone,
				b.customer.email,
				t("number", { value: b.config.number_of_people }),
				t(`order.status.${b.status}`),
				tableLabel(b),
				b.number || b._id,
				b.notes || "",
			]),
		];

		downloadCsv(
			`${restaurantSlug}-bookings-${dayStamp}.csv`,
			rows.map((row) => row.map(csvCell).join(",")).join("\n")
		);
		UI.notification.success(
			t("Bookings downloaded", { defaultValue: "Bookings downloaded" })
		);
	};

	scheduleNotesSave = (bookingId: string, text: string) => {
		const { store } = this.injected;
		const id = `notes-save-${bookingId}`;
		const w = window as unknown as { __cwNotesTimers?: Record<string, number> };
		if (!w.__cwNotesTimers) w.__cwNotesTimers = {};
		if (w.__cwNotesTimers[id]) {
			window.clearTimeout(w.__cwNotesTimers[id]);
		}
		w.__cwNotesTimers[id] = window.setTimeout(async () => {
			try {
				const res = await store.api.booking_update_fields({
					_id: bookingId,
					notes: text,
				});
				if (res.outcome !== 0) {
					throw new Error((res as { message?: string }).message || "save_failed");
				}
				this.setState((s) => ({
					items: s.items.map((x) => (x._id === bookingId ? res.booking : x)),
				}));
				store.updateBookingComplete(res.booking);
			} catch {
				UI.notification.error("Could not save notes");
			}
		}, 450);
	};

	clearDragGhost = () => {
		if (this.dragGhostEl && this.dragGhostEl.parentNode) {
			this.dragGhostEl.parentNode.removeChild(this.dragGhostEl);
		}
		this.dragGhostEl = null;
	};

	applyPlacement = async (
		tableId: string,
		slotMinsRaw: number,
		ctx: {
			day: Moment;
			tz: string;
			startHour: number;
			items: T.Schema.Booking.BookingSchema[];
			tables: T.Schema.Restaurant.Services.DineInTable[];
		},
		bookingId?: string
	) => {
		const movingBookingId = bookingId || this.state.movingBookingId || this.draggingId;
		const { turnMinutes } = this.state;
		const b = ctx.items.find((x) => x._id === movingBookingId);
		if (!b) return;
		if (!canTableRowFitParty(tableId, b.config.number_of_people, ctx.tables)) return;
		const slotIv = getTableBookingSlotIntervalMinutes(this.injected.store.restaurant!);
		const snapped = Math.round(slotMinsRaw / slotIv) * slotIv;
		const dur = this.durationMinsForBooking(b, turnMinutes);
		const rLocal = this.injected.store.restaurant!;
		const weekdayForHours = ctx.day
			.clone()
			.tz(ctx.tz)
			.locale("en")
			.format("dddd") as T.DaysOfWeek;
		const slotsForPlacement = getTableBookingSlotsForWeekday(rLocal, weekdayForHours);
		const winMs = getOpenWindowIntervalsMsForDay(ctx.day.clone(), ctx.tz, slotsForPlacement);
		const dayStartPl = ctx.day.clone().tz(ctx.tz).startOf("day");
		const absStartMin = ctx.startHour * 60 + snapped;
		const tsStartPl = dayStartPl.clone().add(absStartMin, "minutes").valueOf();
		const tsEndPl = tsStartPl + dur * 60_000;
		if (!isBookingIntervalWithinOpenHours(winMs, tsStartPl, tsEndPl)) {
			UI.notification.error(
				this.injected.t("Outside service hours", {
					defaultValue: "Outside service hours",
				})
			);
			return;
		}
		if (
			!isCalendarPlacementSlotValid({
				anchorTableId: tableId,
				party: b.config.number_of_people,
				bookingId: b._id,
				existingTableIds: b.table_ids,
				tables: ctx.tables,
				items: ctx.items,
				day: ctx.day,
				tz: ctx.tz,
				startHour: ctx.startHour,
				slotMins: snapped,
				durationMins: dur,
				turnMinutes,
				tableBlocks: this.getTableBlocks(),
			})
		) {
			return;
		}
		const dayDateStr = ctx.day.clone().tz(ctx.tz).format(FORMATS.moment.date);
		const newTs = ctx.day
			.clone()
			.tz(ctx.tz)
			.startOf("day")
			.add(ctx.startHour * 60 + snapped, "minutes")
			.valueOf();
		const tableIds = resolveCalendarPlacementTableIds({
			anchorTableId: tableId,
			party: b.config.number_of_people,
			bookingId: b._id,
			existingTableIds: b.table_ids,
			tables: ctx.tables,
			items: ctx.items,
			slotStartMs: newTs,
			durationMins: dur,
			turnMinutes,
			tz: ctx.tz,
			dayDateStr,
			tableBlocks: this.getTableBlocks(),
		});
		if (!tableIds?.length) return;

		/** Same threshold as cloudtable `isTimeChanged` (> 1 minute). */
		if (Math.abs(newTs - b.config.timestamp) > 60_000) {
			this.setState({
				adjustmentPending: {
					bookingId: b._id,
					newTs,
					tableIds,
				},
				movingBookingId: null,
			});
			return;
		}

		await this.commitPlacementFields(b._id, tableIds, newTs, b.config.timestamp, {
			notifyGuest: false,
		});
	};

	commitPlacementFields = async (
		bookingId: string,
		tableIds: string[],
		newTs: number,
		originalTs: number,
		opts: { notifyGuest: boolean }
	) => {
		const { store } = this.injected;
		const payload: T.API.DashboardBookingUpdateFieldsRequest = {
			_id: bookingId,
			table_ids: tableIds,
		};
		if (Math.abs(newTs - originalTs) >= 60_000) {
			payload.config_timestamp = newTs;
			payload.skip_customer_email = !opts.notifyGuest;
		}
		try {
			const res = await store.api.booking_update_fields(payload);
			if (res.outcome !== 0) {
				throw new Error((res as { message?: string }).message || "move_failed");
			}
			this.setState((s) => ({
				items: s.items.map((x) => (x._id === bookingId ? res.booking : x)),
				movingBookingId: null,
				selectedId: null,
				adjustmentPending: null,
			}));
			store.updateBookingComplete(res.booking);
			UI.notification.success("Booking placed");
		} catch {
			UI.notification.error("Could not move booking");
		}
	};

	finalizeTimeAdjustment = async (notifyGuest: boolean) => {
		const p = this.state.adjustmentPending;
		if (!p) return;
		const b = this.state.items.find((x) => x._id === p.bookingId);
		if (!b) {
			this.setState({ adjustmentPending: null });
			return;
		}
		await this.commitPlacementFields(p.bookingId, p.tableIds, p.newTs, b.config.timestamp, {
			notifyGuest,
		});
	};

	cancelTimeAdjustment = () => {
		this.draggingId = null;
		this.setState({
			adjustmentPending: null,
			movingBookingId: null,
			selectedId: null,
			statusMenuId: null,
			dragOverKey: null,
		});
	};

	handleCalendarKeyDown = (e: KeyboardEvent) => {
		if (e.key !== "Escape") return;
		if (this.props.suppressSelection) return;
		const el = e.target as HTMLElement | null;
		if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) {
			if (this.state.editingNotesId) {
				e.preventDefault();
				this.setState({ editingNotesId: null });
			}
			return;
		}
		e.preventDefault();
		this.draggingId = null;
		this.setState({
			selectedId: null,
			movingBookingId: null,
			queueConfirmId: null,
			statusMenuId: null,
			dragOverKey: null,
			editingNotesId: null,
			linkingCustomerBookingId: null,
			adjustmentPending: null,
		});
	};

	isCalendarPanIgnoreTarget = (target: EventTarget | null) => {
		const el = target as HTMLElement | null;
		if (!el || typeof el.closest !== "function") return false;
		return !!el.closest("[data-booking-block], .calendar-quick-actions");
	};

	onCalendarPanDown = (e: React.PointerEvent<HTMLDivElement>) => {
		if (e.button !== 0) return;
		if (this.isCalendarPanIgnoreTarget(e.target)) return;
		this.panPointerId = e.pointerId;
		this.panStartX = e.clientX;
		this.panScrollLeft = this.scrollAreaEl?.scrollLeft || 0;
		this.panMoved = false;
	};

	onCalendarPanMove = (e: React.PointerEvent<HTMLDivElement>) => {
		if (this.panPointerId !== e.pointerId || !this.scrollAreaEl) return;
		const dx = e.clientX - this.panStartX;
		if (!this.panMoved && Math.abs(dx) < 8) return;
		if (!this.panMoved) {
			this.panMoved = true;
			try {
				e.currentTarget.setPointerCapture(e.pointerId);
			} catch {
				/* ignore */
			}
		}
		e.preventDefault();
		this.scrollAreaEl.scrollLeft = this.panScrollLeft - dx;
	};

	onCalendarPanUp = (e: React.PointerEvent<HTMLDivElement>) => {
		if (this.panPointerId !== e.pointerId) return;
		this.panPointerId = null;
		window.setTimeout(() => {
			this.panMoved = false;
		}, 0);
	};

	placementHoverKey = (tableId: string, slotMins: number) => `${tableId}::${slotMins}`;

	setPlacementHover = (tableId: string, slotMins: number) => {
		const key = this.placementHoverKey(tableId, slotMins);
		if (this.state.dragOverKey !== key) {
			this.setState({ dragOverKey: key });
		}
	};

	handleCreateAtSlotClick = (
		slotMins: number,
		ctx: { day: Moment; tz: string; startHour: number; tableId?: string }
	) => {
		const cb = this.props.onRequestCreateAtSlot;
		if (!cb) return;
		const dateStr = ctx.day.clone().tz(ctx.tz).format(FORMATS.moment.date);
		const absStartMin = ctx.startHour * 60 + slotMins;
		const timeStr = ctx.day
			.clone()
			.tz(ctx.tz)
			.startOf("day")
			.add(absStartMin, "minutes")
			.format(FORMATS.moment.time);
		cb({ date: dateStr, time: timeStr, table_id: ctx.tableId });
	};

	updateBookingStatus = async (
		b: T.Schema.Booking.BookingSchema,
		status: T.Schema.Booking.BookingStatuses,
		e: React.MouseEvent
	) => {
		e.stopPropagation();
		const { store } = this.injected;
		try {
			const res = await store.api.booking_update_status({
				_id: b._id,
				status,
			});
			if (res.outcome !== 0) {
				throw new Error((res as { message?: string }).message || "status_failed");
			}
			this.setState((s) => ({
				items: s.items.map((x) => (x._id === b._id ? res.booking : x)),
				selectedId: null,
				editingNotesId: null,
				docketBooking: status === "seated" ? res.booking : s.docketBooking,
			}));
			store.updateBookingComplete(res.booking);
		} catch {
			UI.notification.error("Could not update status");
		}
	};

	linkBookingToCustomer = async (bookingId: string, customerId: string) => {
		const { store } = this.injected;
		try {
			const res = await store.api.booking_update_fields({
				_id: bookingId,
				customer_id: customerId,
			});
			if (res.outcome !== 0) {
				throw new Error((res as { message?: string }).message || "link_failed");
			}
			this.setState((s) => ({
				items: s.items.map((x) =>
					x._id === bookingId ? res.booking : x
				),
				linkingCustomerBookingId: null,
				selectedId: null,
			}));
			store.updateBookingComplete(res.booking);
			UI.notification.success(
				this.injected.t("Customer linked", {
					defaultValue: "Customer linked",
				})
			);
		} catch {
			UI.notification.error(
				this.injected.t("Could not link customer", {
					defaultValue: "Could not link customer",
				})
			);
		}
	};

	seatGuest = async (b: T.Schema.Booking.BookingSchema, e: React.MouseEvent) => {
		e.stopPropagation();
		const { store } = this.injected;
		try {
			const res = await store.api.booking_update_status({
				_id: b._id,
				status: "seated",
			});
			if (res.outcome !== 0) {
				throw new Error((res as { message?: string }).message || "seat_failed");
			}
			this.setState((s) => ({
				items: s.items.map((x) => (x._id === b._id ? res.booking : x)),
				selectedId: null,
				editingNotesId: null,
				docketBooking: res.booking,
			}));
			store.updateBookingComplete(res.booking);
		} catch {
			UI.notification.error("Could not update seating");
		}
	};

	closeDocket = () => this.setState({ docketBooking: null });

	printDocket = async (printerId: string) => {
		const { docketBooking } = this.state;
		if (!docketBooking) return;
		const { store } = this.injected;
		const r = store.restaurant!;
		try {
			Untrusive.start();
			await store.api.print_receipt({
				type: "booking",
				restaurant_id: r._id,
				type_id: docketBooking._id,
				printer_id: printerId,
				job_id: nanoid(),
			});
			UI.notification.success("Docket sent to printer");
		} catch (e) {
			logger.captureException(e);
			UI.notification.error("Failed to print docket, try again");
		} finally {
			Untrusive.stop();
		}
	};

	printDocketWithQR = () => {
		const { docketBooking } = this.state;
		if (!docketBooking) return;
		const { store, t } = this.injected;
		const r = store.restaurant!;
		const tables = r.settings.services.dine_in?.tables || [];
		const docketTableId = docketBooking.table_ids?.[0] || null;
		const docketTable = docketTableId ? tables.find((x) => x._id === docketTableId) : null;
		const tableHeadline = liveTableDocketTableHeadline(docketBooking.table_ids, tables);
		const tableQrUrl = docketTableId
			? `${this.getStoreUrl()}?table_id=${docketTableId}`
			: null;
		const dateStr: string = t("datetimeFromTimestamp", { value: docketBooking.config.timestamp }) || "";
		const partyStr = `${t("number", { value: docketBooking.config.number_of_people }) || docketBooking.config.number_of_people} ${t("guests", { defaultValue: "guests" }) || "guests"}`;

		const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

		const qrMarkup =
			tableQrUrl &&
			ReactDOMServer.renderToStaticMarkup(
				<QRCode value={tableQrUrl} size={120} renderAs="svg" />,
			);

		const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Docket</title>
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; display: flex; justify-content: center; padding: 24px; }
.card { max-width: 380px; width: 100%; text-align: center; }
.table-band { padding: 20px 0 24px; margin-bottom: 8px; border-bottom: 2px dashed #cbd5e1; }
.table-title { font-size: 2rem; font-weight: 900; color: #0f172a; letter-spacing: -0.03em; line-height: 1.1; }
.venue { margin-top: 6px; font-size: 0.75rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.1em; }
.guest-block { padding: 20px 0 12px; }
.guest-label { font-size: 0.75rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 4px; }
.guest-name { font-size: 1.4rem; font-weight: 900; color: #0f172a; letter-spacing: -0.02em; }
.party { margin-top: 4px; font-size: 0.9rem; font-weight: 600; color: #334155; }
.hint-box { margin-top: 16px; padding: 16px; background: #f8fafc; border-radius: 16px; border: 2px solid #e2e8f0; }
.hint-text { font-size: 0.85rem; font-weight: 700; color: #64748b; }
.qr-section { margin-top: 20px; display: flex; flex-direction: column; align-items: center; gap: 6px; }
.qr-section svg { display: block; }
.qr-label { font-size: 0.65rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.08em; }
</style>
</head><body>
<div class="card">
<div class="table-band">
<div class="table-title">${esc(tableHeadline)}</div>
<div class="venue">${esc(r.name || "")}</div>
</div>
<div class="guest-block">
<div class="guest-label">Guest</div>
<div class="guest-name">${esc(docketBooking.customer.name)}</div>
<div class="party">${esc(partyStr)}</div>
</div>
<div class="hint-box"><div class="hint-text">${esc(dateStr)}</div></div>
${qrMarkup ? `<div class="qr-section">${qrMarkup}<div class="qr-label">Table QR</div></div>` : ""}
</div>
<script>window.onload=function(){window.print();try{window.close();}catch(e){}};<\/script>
</body></html>`;

		const w = window.open("", "_blank", "width=420,height=650");
		if (!w) return;
		w.document.write(html);
		w.document.close();
	};

	getStoreUrl = (): string => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const storeHost = store.reseller!.store_host;
		return r.domain
			? `https://${r.domain}`
			: `https://${r.subdomain}.${storeHost}`;
	};

	renderBookingBlock(
		b: T.Schema.Booking.BookingSchema,
		ctx: {
			left: number;
			width: number;
			top: number;
			laneHeight: number;
			items: T.Schema.Booking.BookingSchema[];
			tables: T.Schema.Restaurant.Services.DineInTable[];
			t: WithTranslation["t"];
		},
		reactKey: string,
		showQuickActions: boolean,
		joinRole: BookingJoinRowRole = "single"
	) {
		const {
			selectedId,
			editingNotesId,
			notesDraft,
			movingBookingId,
			linkingCustomerBookingId,
			statusMenuId,
		} = this.state;
		const placingId = this.state.movingBookingId || this.draggingId;
		const rid = this.injected.store.restaurant!._id;
		const isSeated = b.status === "seated";
		const isComplete = b.status === "complete";
		const isCancelled = b.status === "cancelled";
		const seatedStyle = isSeated || isComplete;
		const blockStatus: "active" | "done" | "cancelled" =
			isSeated || isComplete ? "done" : isCancelled ? "cancelled" : "active";
		const canMovePlacement =
			(b.status === "confirmed" || b.status === "unconfirmed") &&
			joinRole !== "secondary";
		const joinedTableLabel =
			joinRole === "primary"
				? formatBookingTableLabel(b.table_ids, ctx.tables)
				: null;
		const canLinkCustomer =
			b.status !== "cancelled" &&
			b.status !== "complete" &&
			b.status !== "seated" &&
			!b.customer_linked &&
			!b.customer?._id;
		const selected = !this.props.suppressSelection && selectedId === b._id;
		const z = selected ? 24 : 12;
		const dim = !!placingId && placingId !== b._id;
		const pe = placingId && placingId !== b._id ? "none" : "auto";

		return (
			<BookingBlockOuter
				key={reactKey}
				data-booking-block="1"
				$left={ctx.left}
				$width={ctx.width}
				$top={ctx.top}
				$laneHeight={ctx.laneHeight}
				$z={z}
				$pe={pe}
				style={
					dim
						? { opacity: 0.12, transform: "scale(0.96)" }
						: undefined
				}
			>
				{selected && !placingId && showQuickActions && (
					<QuickActionsBar
						className="calendar-quick-actions"
						onMouseDown={(e) => e.stopPropagation()}
						onClick={(e) => e.stopPropagation()}
					>
						<StatusMenuWrap>
							<QuickActionPrimary
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									this.setState((s) => ({
										statusMenuId: s.statusMenuId === b._id ? null : b._id,
									}));
								}}
							>
								{ctx.t("Status", { defaultValue: "Status" })}
							</QuickActionPrimary>
							{statusMenuId === b._id ? (
								<StatusMenuList onClick={(e) => e.stopPropagation()}>
									{([
										["unconfirmed", "Unconfirmed"],
										["confirmed", "Confirmed"],
										["seated", "Seated"],
										["complete", "Complete"],
										["cancelled", "Cancelled"],
									] as Array<[T.Schema.Booking.BookingStatuses, string]>).map(
										([status, label]) =>
											status === b.status ? null : (
												<StatusMenuItem
													key={status}
													type="button"
													$danger={status === "cancelled"}
													onClick={(e) => {
														this.setState({ statusMenuId: null });
														void this.updateBookingStatus(b, status, e);
													}}
												>
													{ctx.t(label, { defaultValue: label })}
												</StatusMenuItem>
											)
									)}
								</StatusMenuList>
							) : null}
						</StatusMenuWrap>
						{isSeated || isComplete ? (
							<QuickActionDocket
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									this.setState({
										docketBooking: b,
										selectedId: null,
										linkingCustomerBookingId: null,
									});
								}}
							>
								{ctx.t("Docket", { defaultValue: "Docket" })}
							</QuickActionDocket>
						) : null}
						{canMovePlacement && !seatedStyle && (
							<QuickActionGhost
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									this.setState({
										movingBookingId: b._id,
										selectedId: null,
										queueConfirmId: null,
										editingNotesId: null,
										linkingCustomerBookingId: null,
									});
								}}
							>
								{ctx.t("Move", { defaultValue: "Move" })}
							</QuickActionGhost>
						)}
						{canLinkCustomer && (
							<QuickActionGhost
								type="button"
								$active={linkingCustomerBookingId === b._id}
								onClick={(e) => {
									e.stopPropagation();
									this.setState((s) => ({
										linkingCustomerBookingId:
											s.linkingCustomerBookingId === b._id ? null : b._id,
										editingNotesId: null,
									}));
								}}
							>
								{ctx.t("Link customer", { defaultValue: "Link customer" })}
							</QuickActionGhost>
						)}
						<QuickActionGhost
							type="button"
							$active={editingNotesId === b._id}
							onClick={(e) => {
								e.stopPropagation();
								this.setState((s) => {
									const closing = s.editingNotesId === b._id;
									return {
										editingNotesId: closing ? null : b._id,
										notesDraft: closing ? "" : b.notes || "",
										linkingCustomerBookingId: null,
									};
								});
							}}
						>
							{ctx.t("Notes", { defaultValue: "Notes" })}
							{b.notes?.trim() ? (
								<BookingNotesIndicator notes={b.notes} compact style={{ marginLeft: 6 }} />
							) : null}
						</QuickActionGhost>
						{linkingCustomerBookingId === b._id && (
							<LinkCustomerPopover onClick={(e) => e.stopPropagation()}>
								<BookingCustomerSearch
									active={linkingCustomerBookingId === b._id}
									restaurantId={rid}
									onPick={(p) => {
										void this.linkBookingToCustomer(b._id, p._id);
									}}
								/>
							</LinkCustomerPopover>
						)}
						{editingNotesId === b._id && (
							<NotesPopover onClick={(e) => e.stopPropagation()}>
								<textarea
									className="form-control"
									style={{
										width: "100%",
										minHeight: 72,
										fontSize: 12,
										borderRadius: 10,
									}}
									value={notesDraft}
									placeholder={ctx.t("Guest notes", {
										defaultValue: "Guest notes",
									})}
									onChange={(e) => {
										const v = e.target.value;
										this.setState({ notesDraft: v });
										this.scheduleNotesSave(b._id, v);
									}}
								/>
							</NotesPopover>
						)}
					</QuickActionsBar>
				)}
				<BookingBlockBtn
					type="button"
					draggable={canMovePlacement && !seatedStyle}
					$status={blockStatus}
					$ring={selected}
					$dragging={movingBookingId === b._id && this.draggingId !== b._id}
					$joinSecondary={joinRole === "secondary"}
					onDragStart={(e) => {
						if (!canMovePlacement || seatedStyle) {
							e.preventDefault();
							return;
						}
						e.stopPropagation();
						this.dragOccurred = true;
						this.draggingId = b._id;
						e.dataTransfer.setData("text/plain", b._id);
						e.dataTransfer.effectAllowed = "move";
						const src = e.currentTarget;
						const ghost = src.cloneNode(true) as HTMLElement;
						ghost.style.width = `${src.offsetWidth}px`;
						ghost.style.height = `${src.offsetHeight}px`;
						ghost.style.position = "absolute";
						ghost.style.top = "-9999px";
						ghost.style.left = "-9999px";
						ghost.style.opacity = "0.92";
						ghost.style.transform = "rotate(-4deg) scale(1.04)";
						ghost.style.boxShadow = "0 18px 36px rgba(15,23,42,0.28)";
						ghost.style.pointerEvents = "none";
						document.body.appendChild(ghost);
						this.clearDragGhost();
						this.dragGhostEl = ghost;
						e.dataTransfer.setDragImage(ghost, 24, 18);
						window.setTimeout(() => {
							if (this.draggingId !== b._id) return;
							this.setState({
								movingBookingId: b._id,
								selectedId: null,
								queueConfirmId: null,
								editingNotesId: null,
								linkingCustomerBookingId: null,
								statusMenuId: null,
							});
						}, 0);
					}}
					onDragEnd={() => {
						this.clearDragGhost();
						this.draggingId = null;
						this.setState({ dragOverKey: null });
						window.setTimeout(() => {
							this.dragOccurred = false;
						}, 0);
					}}
					onClick={(e) => {
						if (this.dragOccurred) {
							e.preventDefault();
							e.stopPropagation();
							this.dragOccurred = false;
							return;
						}
						this.toggleBookingSelect(b, e);
					}}
				>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
							minWidth: 0,
						}}
					>
						<BlockTitle $strike={isSeated}>
							{b.number} - {b.customer.name}
						</BlockTitle>
						<BookingNotesIndicator notes={b.notes} compact />
					</div>
					{joinedTableLabel ? (
						<BlockTableLabel title={joinedTableLabel}>{joinedTableLabel}</BlockTableLabel>
					) : null}
					<BlockMeta>
						<BlockMetaItem>
							<FaRegClock />
							{ctx.t("timeFromTimestamp", { value: b.config.timestamp, defaultValue: ctx.t("datetimeFromTimestamp", { value: b.config.timestamp }) })}
						</BlockMetaItem>
						<BlockMetaItem>
							<FaUser />
							{ctx.t("number", { value: b.config.number_of_people })} pax
						</BlockMetaItem>
					</BlockMeta>
				</BookingBlockBtn>
			</BookingBlockOuter>
		);
	}

	loadDay = async (day: Moment) => {
		const { store } = this.injected;
		const r = store.restaurant!;
		const tz = r.settings.region.timezone;
		const start = day.clone().tz(tz).startOf("day").valueOf();
		const end = day.clone().tz(tz).endOf("day").valueOf();
		this.setState({
			loading: true,
			day,
			datePickerOpen: false,
			datePickerMonth: day.clone(),
			movingBookingId: null,
			queueConfirmId: null,
			adjustmentPending: null,
			linkingCustomerBookingId: null,
			editingNotesId: null,
		});
		try {
			const response = await store.api.bookings_find({
				page: 1,
				limit: 500,
				sort: { "config.timestamp": 1 },
				query: {
					restaurant_id: r._id,
					"config.timestamp": { $gte: start, $lte: end },
				},
			});
			if (response.outcome) {
				throw new Error(response.message);
			}
			const dayKey = day.clone().tz(tz).format(FORMATS.moment.date);
			this.setState({ items: response.items, loading: false }, () =>
				this.scheduleTimelineScroll(dayKey)
			);
		} catch (e) {
			const dayKey = day.clone().tz(tz).format(FORMATS.moment.date);
			this.setState({ items: [], loading: false }, () =>
				this.scheduleTimelineScroll(dayKey)
			);
		}
	};

	render() {
		const { store, t } = this.injected;
		const r = store.restaurant!;
		const tz = r.settings.region.timezone;
		const {
			loading,
			items,
			day,
			selectedId,
			turnMinutes,
			movingBookingId,
			queueConfirmId,
			docketBooking,
			adjustmentPending,
			datePickerOpen,
			datePickerMonth,
			gridWidth,
			tableSearch,
			tableFilter,
			dragOverKey,
			blockTableMode,
		} = this.state;
		const tableBlocks = this.getTableBlocks();
		const dayDateStr = day.clone().tz(tz).format(FORMATS.moment.date);
		const dayTableBlocks = tableBlocks.filter((b) => b.date === dayDateStr);
		const onRequestCreateAtSlot = this.props.onRequestCreateAtSlot;
		const narrow = store.view.screen_width < 768;
		const tableColW = narrow
			? TABLE_COL_WIDTH_NARROW_PX
			: TABLE_COL_WIDTH_WIDE_PX;

		const { startHour, endHour } = getTimelineBounds(r, day, tz);
		const numHours = Math.max(1, endHour - startHour);
		const baseSlotSize = narrow ? 80 : 120;
		const visibleTimelineWidth = Math.max(0, gridWidth - tableColW);
		const minTimeline = numHours * baseSlotSize;
		const slotSize =
			visibleTimelineWidth > minTimeline
				? Math.floor(visibleTimelineWidth / numHours)
				: baseSlotSize;
		const timelineInnerWidth = numHours * slotSize;
		const minuteSize = slotSize / 60;
		const totalMinutes = numHours * 60;
		const slotIntervalMins = getTableBookingSlotIntervalMinutes(r);
		const minorGrid = slotIntervalMins * minuteSize;

		const label = day.clone().tz(tz).locale(store.intl.s.lng).format("dddd, D MMM YYYY");
		const isToday = day.clone().tz(tz).isSame(moment().tz(tz), "day");
		const pickerMonth = datePickerMonth.clone().tz(tz).locale(store.intl.s.lng).startOf("month");
		const pickerGridStart = pickerMonth.clone().startOf("week");
		const pickerWeekdays = Array.from({ length: 7 }, (_, i) =>
			pickerGridStart.clone().add(i, "day").format("dd")
		);
		const pickerDays = Array.from({ length: 42 }, (_, i) =>
			pickerGridStart.clone().add(i, "day")
		);

		let nowPos: number | null = null;
		if (isToday) {
			const now = moment().tz(tz);
			const minsSinceStart = now.hours() * 60 + now.minutes() - startHour * 60;
			if (minsSinceStart >= 0 && minsSinceStart <= totalMinutes) {
				nowPos = tableColW + minsSinceStart * minuteSize;
			}
		}

		const timeLabels: string[] = [];
		for (let h = startHour; h < endHour; h++) {
			const hour = ((h % 24) + 24) % 24;
			timeLabels.push(
				day
					.clone()
					.tz(tz)
					.hour(hour)
					.minute(0)
					.second(0)
					.locale(store.intl.s.lng)
					.format("LT")
			);
		}

		/** Same filter as cloudtable unallocated: no table or still unconfirmed. */
		const queueItems = items.filter(
			(b) =>
				b.status !== "cancelled" &&
				b.status !== "complete" &&
				(!b.table_ids?.length || b.status === "unconfirmed")
		);

		const startMinuteFromBooking = (b: T.Schema.Booking.BookingSchema) => {
			const m = moment(b.config.timestamp).tz(tz);
			return m.hours() * 60 + m.minutes() - startHour * 60;
		};

		const placingId = movingBookingId || this.draggingId;
		const durationMinsFn = (b: T.Schema.Booking.BookingSchema) =>
			this.durationMinsForBooking(b, turnMinutes);
		const laneHeight = BOOKING_LANE_HEIGHT_PX;
		const mbPlace = placingId
			? items.find((x) => x._id === placingId)
			: null;

		const tables: T.Schema.Restaurant.Services.DineInTable[] =
			r.settings.services?.dine_in?.tables || [];
		const tableSectionValue = (tbl: T.Schema.Restaurant.Services.DineInTable) =>
			String(
				tbl.area ||
					(tbl as T.ObjectAny).section ||
					(tbl as T.ObjectAny).position ||
					(tbl as T.ObjectAny).location ||
					""
			).trim();
		const tableSectionOptions = Array.from(
			new Set(tables.map(tableSectionValue).filter(Boolean))
		);
		const filteredTables = tables.filter((tbl) => {
			const q = tableSearch.trim().toLowerCase();
			const nameMatch = !q || (tbl.name || "").toLowerCase().includes(q);
			const seats = tbl.seats && tbl.seats > 0 ? tbl.seats : 4;
			if (tableFilter.startsWith("seats:")) {
				return nameMatch && seats >= Number(tableFilter.slice(6));
			}
			if (tableFilter.startsWith("section:")) {
				return nameMatch && tableSectionValue(tbl) === tableFilter.slice(8);
			}
			return nameMatch;
		});

		const placementDetails =
			placingId && mbPlace
				? computePlacementHighlight(mbPlace, {
						day,
						startHour,
						totalMinutes,
						tz,
						tables,
						items,
						durationMinsFn,
						slotIntervalMins,
						turnMinutes,
						tableBlocks,
					})
				: null;

		const weekdayForHours = day
			.clone()
			.tz(tz)
			.locale("en")
			.format("dddd") as T.DaysOfWeek;
		const tableBookingSlots = getTableBookingSlotsForWeekday(r, weekdayForHours);
		const openWindowsMs = getOpenWindowIntervalsMsForDay(day.clone(), tz, tableBookingSlots);
		const svcTb = r.settings.services.table_booking;
		const maxPRef = svcTb.max_people || svcTb.limit_number_of_people;
		const minPRef = svcTb.min_people || 1;
		let referencePartyForSlots = Math.max(minPRef, 2);
		if (maxPRef && referencePartyForSlots > maxPRef) {
			referencePartyForSlots = maxPRef;
		}
		const closedSegsPx = buildClosedTimelineSegmentsPx({
			day: day.clone(),
			tz,
			startHour,
			totalMinutes,
			minuteSize,
			slotStepMins: slotIntervalMins,
			openWindowsMs,
		});

		return (
			<div>
				{/* ── Calendar toolbar: portalled into parent toolbar row when container provided ── */}
				{(() => {
					const toolbarJSX = (
						<CalendarToolbar>
							<QueueTableSearchWrap>
								<FaSearch style={{ fontSize: 11, color: "#9da4ae", flexShrink: 0 }} />
								<QueueTableSearchInput
									value={tableSearch}
									placeholder={t("Search tables", { defaultValue: "Search tables" })}
									onChange={(e) => this.setState({ tableSearch: e.target.value })}
								/>
							</QueueTableSearchWrap>
							<ToolbarPill>
								<ToolbarIconBtn
									type="button"
									aria-label={t("Previous day", { defaultValue: "Previous day" })}
									onClick={() => this.loadDay(day.clone().subtract(1, "day"))}
								>
									<FaChevronLeft />
								</ToolbarIconBtn>
								<ToolbarTodayBtn
									type="button"
									onClick={this.openDatePicker}
									title={t("Select date", { defaultValue: "Select date" })}
								>
									{day.clone().tz(tz).locale(store.intl.s.lng).format("ddd, MMM D, YYYY")}
								</ToolbarTodayBtn>
								<ToolbarIconBtn
									type="button"
									aria-label={t("Next day", { defaultValue: "Next day" })}
									onClick={() => this.loadDay(day.clone().add(1, "day"))}
								>
									<FaChevronRight />
								</ToolbarIconBtn>
							</ToolbarPill>
							<TurnPill>
								<TurnPillLabel>
									{t("Turn", { defaultValue: "Turn" })}:
								</TurnPillLabel>
								<TurnPillInput
									type="number"
									min={15}
									step={15}
									aria-label={t("Turn minutes", { defaultValue: "Turn minutes" })}
									value={turnMinutes}
									onChange={(e) =>
										this.setState({
											turnMinutes: Math.max(
												15,
												parseInt(e.target.value, 10) || 15
											),
										})
									}
								/>
								<TurnPillLabel style={{ fontWeight: 500, color: "#717680" }}>
									{t("mins", { defaultValue: "mins" })}
								</TurnPillLabel>
							</TurnPill>
							<ToolbarDownloadBtn
								type="button"
								onClick={this.toggleBlockTableMode}
								title={t("Block table hours on the timeline", {
									defaultValue: "Block table hours on the timeline",
								})}
								style={
									blockTableMode
										? {
												background: "#fef3c7",
												borderColor: "#f59e0b",
												color: "#92400e",
											}
										: undefined
								}
							>
								<FaBan />
								{t("Block table", { defaultValue: "Block table" })}
							</ToolbarDownloadBtn>
							<ToolbarDownloadBtn
								type="button"
								onClick={this.openFloorPlanSettings}
								title={t("Edit table floor plan in settings", {
									defaultValue: "Edit table floor plan in settings",
								})}
							>
								<FaTh />
								{t("Floor plan", { defaultValue: "Floor plan" })}
							</ToolbarDownloadBtn>
							<ToolbarDownloadBtn
								type="button"
								onClick={this.downloadDayBookings}
								title={t("Download bookings for this day", {
									defaultValue: "Download bookings for this day",
								})}
							>
								<FaDownload />
								{t("Download", { defaultValue: "Download" })}
							</ToolbarDownloadBtn>
						</CalendarToolbar>
					);
					const { toolbarContainer } = this.props as Props;
					return toolbarContainer
						? ReactDOM.createPortal(toolbarJSX, toolbarContainer)
						: <CalendarPageHeader className="m-b-3">{toolbarJSX}</CalendarPageHeader>;
				})()}


				{datePickerOpen && (
					<DatePickerBackdrop onClick={this.closeDatePicker}>
						<DatePickerCard
							role="dialog"
							aria-modal="true"
							aria-label={t("Select booking date", {
								defaultValue: "Select booking date",
							})}
							onClick={(e) => e.stopPropagation()}
						>
							<DatePickerHeader>
								<DatePickerNavBtn
									type="button"
									aria-label={t("Previous month", {
										defaultValue: "Previous month",
									})}
									onClick={() => this.moveDatePickerMonth(-1)}
								>
									<FaChevronLeft />
								</DatePickerNavBtn>
								<DatePickerMonthTitle>
									{pickerMonth.format("MMMM YYYY")}
								</DatePickerMonthTitle>
								<DatePickerNavBtn
									type="button"
									aria-label={t("Next month", {
										defaultValue: "Next month",
									})}
									onClick={() => this.moveDatePickerMonth(1)}
								>
									<FaChevronRight />
								</DatePickerNavBtn>
							</DatePickerHeader>
							<DatePickerWeekdays>
								{pickerWeekdays.map((d) => (
									<DatePickerWeekday key={d}>{d}</DatePickerWeekday>
								))}
							</DatePickerWeekdays>
							<DatePickerGrid>
								{pickerDays.map((d) => {
									const key = d.format(FORMATS.moment.date);
									const selected = d.isSame(day, "day");
									const today = d.isSame(moment().tz(tz), "day");
									const outside = !d.isSame(pickerMonth, "month");
									return (
										<DatePickerDayBtn
											key={key}
											type="button"
											$selected={selected}
											$today={today}
											$outside={outside}
											onClick={() => this.selectDatePickerDay(d)}
										>
											{d.format("D")}
										</DatePickerDayBtn>
									);
								})}
							</DatePickerGrid>
						</DatePickerCard>
					</DatePickerBackdrop>
				)}

				{loading ? (
					<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", padding: 16 }}><RotateLoader size={4} /></div>
				) : (
					<CalendarShell>
						<QueuePanel>
							{/* ── Filter header: All Tables select ── */}
							<QueueFilterHeader>
								<QueueFilterSelect
									value={tableFilter}
									onChange={(e) => this.setState({ tableFilter: e.target.value })}
								>
									<option value="">{t("All Tables", { defaultValue: "All Tables" })}</option>
									<optgroup label={t("Seats", { defaultValue: "Seats" })}>
										{[2, 4, 6, 8, 10].map((n) => (
											<option key={`seats:${n}`} value={`seats:${n}`}>
												{n}+ {t("seats", { defaultValue: "seats" })}
											</option>
										))}
									</optgroup>
									{tableSectionOptions.length > 0 ? (
										<optgroup label={t("Section", { defaultValue: "Section" })}>
											{tableSectionOptions.map((section) => (
												<option key={`section:${section}`} value={`section:${section}`}>
													{section}
												</option>
											))}
										</optgroup>
									) : null}
								</QueueFilterSelect>
							</QueueFilterHeader>

							{/* ── Content: title + cards + legend ── */}
							<QueueContentBody>
								<QueuePendingTitle>
									{t("Pending queue", { defaultValue: "Pending queue" })} ({queueItems.length})
								</QueuePendingTitle>


								<QueueList>
									{queueItems.length === 0 ? (
										<QueueEmptyState>
											<QueueEmptyIcon aria-hidden>
												<FaBookOpen />
											</QueueEmptyIcon>
											<div>
												<QueueEmptyTitle>
													{t("No reservation", { defaultValue: "No reservation" })}
												</QueueEmptyTitle>
												<QueueEmptySubtext>
													{t("Your reservation list is currently empty.", {
														defaultValue: "Your reservation list is currently empty.",
													})}
												</QueueEmptySubtext>
											</div>
										</QueueEmptyState>
									) : (
										queueItems.map((b) => {
											const isUnconfirmed = b.status === "unconfirmed";
											const isExpanded = this.state.queueExpandId === b._id;
											const isActive = isExpanded || placingId === b._id;

											const isPlacing = placingId === b._id;
											const cancelPlacement = () => {
												this.draggingId = null;
												this.setState({
													movingBookingId: null,
													queueConfirmId: null,
													queueExpandId: null,
													linkingCustomerBookingId: null,
													selectedId: null,
													statusMenuId: null,
													dragOverKey: null,
													adjustmentPending: null,
												});
											};

											return (
												<QueueCardRoot
													key={b._id}
													$active={isActive}
													$unconfirmed={isUnconfirmed}
													role="button"
													tabIndex={0}
													onClick={(e) => {
														e.stopPropagation();
														if (isPlacing) {
															cancelPlacement();
														} else {
															this.setState((s) => ({
																queueExpandId: s.queueExpandId === b._id ? null : b._id,
															}));
														}
													}}
													onKeyDown={(e) => {
														if (e.key === "Enter" || e.key === " ") {
															e.preventDefault();
															if (isPlacing) {
																cancelPlacement();
															} else {
																this.setState((s) => ({
																	queueExpandId: s.queueExpandId === b._id ? null : b._id,
																}));
															}
														}
													}}
												>
													<QueueCardTop>
														<div style={{ minWidth: 0, flex: 1 }}>
															<div
																style={{
																	display: "flex",
																	alignItems: "center",
																	gap: 6,
																	minWidth: 0,
																}}
															>
																<QueueGuest $unconfirmed={isUnconfirmed}>
																	{b.number} - {b.customer.name}
																</QueueGuest>
																<BookingNotesIndicator notes={b.notes} compact />
															</div>
															<QueueMetaRow>
																<QueueTime>
																	{t("datetimeFromTimestamp", { value: b.config.timestamp })}
																</QueueTime>
																<QueueParty>
																	{t("number", { value: b.config.number_of_people })} pax
																</QueueParty>
															</QueueMetaRow>
															<div style={{ marginTop: 6 }}>
																{isUnconfirmed ? (
																	<QueueBadge $variant="assign">
																		{t("Assign Table", { defaultValue: "Assign Table" })}
																	</QueueBadge>
																) : (
																	<QueueBadge $variant="ready">
																		{t("Ready", { defaultValue: "Ready" })}
																	</QueueBadge>
																)}
															</div>
														</div>
														{/* Chevron toggle button */}
														<button
															type="button"
															aria-label={isExpanded ? "Collapse" : "Expand"}
															onClick={(e) => {
																e.stopPropagation();
																this.setState((s) => ({
																	queueExpandId: s.queueExpandId === b._id ? null : b._id,
																}));
															}}
															style={{
																width: 32,
																height: 32,
																border: "1px solid #d5d7da",
																borderRadius: 8,
																background: "#fff",
																color: "#414651",
																fontSize: 14,
																cursor: "pointer",
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
																flexShrink: 0,
																boxShadow: "0 1px 2px rgba(10,13,18,0.05)",
															}}
														>
															{isExpanded ? <FaChevronUp /> : <FaChevronDown />}
														</button>
													</QueueCardTop>

													{/* Expanded actions */}
													{isExpanded && (
														<div onClick={(e) => e.stopPropagation()}>
															{isUnconfirmed ? (
																<QueueConfirmActions>
																	<QueueConfirmBtn
																		type="button"
																		$primary
																		onClick={(e) =>
																			this.confirmQueueBooking(b, { silent: false }, e)
																		}
																	>
																		{t("Confirm Email", { defaultValue: "Confirm Email" })}
																	</QueueConfirmBtn>
																	<QueueConfirmBtn
																		type="button"
																		onClick={(e) =>
																			this.confirmQueueBooking(b, { silent: true }, e)
																		}
																	>
																		{t("Confirm Silent", { defaultValue: "Confirm Silent" })}
																	</QueueConfirmBtn>
																</QueueConfirmActions>
															) : (
																<QueueAssignSeatBtn
																	type="button"
																	onClick={(e) => {
																		e.stopPropagation();
																		this.setState({ queueExpandId: null });
																		this.handleQueueCardClick(b, e);
																	}}
																>
																	{t("Assign Seat", { defaultValue: "Assign Seat" })}
																</QueueAssignSeatBtn>
															)}
														</div>
													)}
												</QueueCardRoot>
											);
										})
									)}
								</QueueList>

								{/* Legend at bottom of content */}
								<QueueLegendFooter>
									<LegendRow>
										<LegendSwatch $color="#fb2f2f" />
										{t("Active / confirmed", { defaultValue: "Active / confirmed" })}
									</LegendRow>
									<LegendRow>
										<LegendSwatch $color="#009100" />
										{t("Seated / complete", { defaultValue: "Seated / complete" })}
									</LegendRow>
								</QueueLegendFooter>
							</QueueContentBody>
						</QueuePanel>

						<GridWrap $placing={!!placingId}>
							<ScrollArea
								ref={this.setScrollAreaRef}
								style={{
									cursor: placingId ? "crosshair" : undefined,
								}}
								onPointerDown={this.onCalendarPanDown}
								onPointerMove={this.onCalendarPanMove}
								onPointerUp={this.onCalendarPanUp}
								onPointerCancel={this.onCalendarPanUp}
								onClick={() => {
									if (this.panMoved) return;
									this.draggingId = null;
									this.setState({
										selectedId: null,
										movingBookingId: null,
										queueConfirmId: null,
										editingNotesId: null,
										linkingCustomerBookingId: null,
										statusMenuId: null,
									});
								}}
							>
								<div style={{ minWidth: tableColW + timelineInnerWidth }}>
									{items.length === 0 && (
										<div
											className="p-3 text-center small text-muted"
											style={{
												borderBottom: "1px solid #eee",
												background: "#fafafa",
											}}
										>
											{t("No bookings for this day.", {
												defaultValue: "No bookings for this day.",
											})}
										</div>
									)}
									<StickyHeader>
											<CornerCell>{t("Table", { defaultValue: "Table" })}</CornerCell>
											<HourTrack>
												{timeLabels.map((lbl, i) => (
													<HourCell key={i} $w={slotSize}>
														{lbl}
													</HourCell>
												))}
											</HourTrack>
										</StickyHeader>

										<div style={{ position: "relative" }}>
											{nowPos !== null && <NowLine $left={nowPos} />}

											{filteredTables.map((tbl, idx) => {
												const cap = tbl.seats && tbl.seats > 0 ? tbl.seats : 4;
												const title = tbl.name?.trim() || `T${idx + 1}`;
												const isTableSuitable = mbPlace
													? canTableRowFitParty(
															tbl._id,
															mbPlace.config.number_of_people,
															tables
														)
													: true;
												const rowItems = items.filter((b) =>
													b.table_ids?.includes(tbl._id)
												);
												const lanesRow = assignLanes(
													rowItems,
													startMinuteFromBooking,
													durationMinsFn
												);
												const maxLR = rowItems.length
													? Math.max(...rowItems.map((b) => lanesRow[b._id] ?? 0))
													: 0;
												const rowH = Math.max(
													MIN_TABLE_ROW_PX,
													(maxLR + 1) * laneHeight + 12
												);
												const laneStackHeight = (maxLR + 1) * laneHeight;
												const laneTopBase = Math.max(4, (rowH - laneStackHeight) / 2);
												const dimRow = !!placingId && !!mbPlace && !isTableSuitable;
												return (
													<GridRow
														key={tbl._id || idx}
														style={{
															opacity: dimRow ? 0.22 : 1,
															filter: dimRow ? "grayscale(0.9)" : undefined,
															pointerEvents: dimRow ? "none" : undefined,
															transition: "opacity 0.2s ease",
														}}
													>
														<RowLabel
															style={
																placingId && mbPlace && isTableSuitable
																	? {
																			background: "rgba(238, 242, 255, 0.98)",
																			borderRightColor: "#c7d2fe",
																		}
																	: undefined
															}
														>
															<RowLabelTitle>{title}</RowLabelTitle>
															<RowLabelMeta>
																{t("number", { value: cap })}{" "}
																{t("seats", { defaultValue: "seats" })}
															</RowLabelMeta>
														</RowLabel>
														<TimelineTrack
															$w={timelineInnerWidth}
															$slot={slotSize}
															$minor={minorGrid}
															style={{ height: rowH }}
															onMouseLeave={() => {
																if (this.draggingId) return;
																if (this.state.dragOverKey) {
																	this.setState({ dragOverKey: null });
																}
															}}
														>
															{closedSegsPx.map((seg, si) => (
																<ClosedOverlayStrip
																	key={`cov-${tbl._id}-${si}`}
																	$left={seg.left}
																	$width={seg.width}
																/>
															))}
															{dayTableBlocks
																.filter(
																	(
																		block: T.Schema.Restaurant.Services.TableBookingBlock
																	) => block.table_ids?.includes(tbl._id)
																)
																.map(
																	(
																		block: T.Schema.Restaurant.Services.TableBookingBlock
																	) => {
																	const px = tableBlockTimelinePx({
																		block,
																		tz,
																		startHour,
																		minuteSize,
																	});
																	if (!px) return null;
																	const label =
																		block.reason?.trim() ||
																		t("Blocked", {
																			defaultValue: "Blocked",
																		});
																	return (
																		<TableBlockStrip
																			key={block._id}
																			type="button"
																			$left={px.left}
																			$width={px.width}
																			title={label}
																			onClick={(e) => {
																				e.stopPropagation();
																				void this.removeTableBlock(
																					block._id
																				);
																			}}
																		>
																			{label}
																		</TableBlockStrip>
																	);
																}
																)}
															{rowItems.map((b) => {
																const startM = startMinuteFromBooking(b);
																const left = Math.max(0, startM * minuteSize);
																const width =
																	this.durationMinsForBooking(b, turnMinutes) *
																	minuteSize;
																const lane = lanesRow[b._id] ?? 0;
																const top = laneTopBase + lane * laneHeight;
																const joinRole = bookingJoinRowRole(b, tbl._id);
																return this.renderBookingBlock(
																	b,
																	{
																		left,
																		width,
																		top,
																		laneHeight,
																		items,
																		tables,
																		t,
																	},
																	`${b._id}-${tbl._id}`,
																	joinRole !== "secondary",
																	joinRole
																);
															})}
															{placingId &&
																(() => {
																	const mb = items.find(
																		(x) => x._id === placingId
																	);
																	if (!mb) return null;
																	const dur = this.durationMinsForBooking(
																		mb,
																		turnMinutes
																	);
																	const capOk = canTableRowFitParty(
																		tbl._id,
																		mb.config.number_of_people,
																		tables
																	);
																	const dayStartMb = day
																		.clone()
																		.tz(tz)
																		.startOf("day");
																	const numSlots = Math.max(
																		1,
																		Math.floor(
																			totalMinutes / slotIntervalMins
																		)
																	);
																	return Array.from(
																		{ length: numSlots },
																		(_, i) => {
																			const slotMins = i * slotIntervalMins;
																			const absStartMinMb =
																				startHour * 60 + slotMins;
																			const tsStartMb = dayStartMb
																				.clone()
																				.add(absStartMinMb, "minutes")
																				.valueOf();
																			const tsEndMb = tsStartMb + dur * 60_000;
																			const slotOpen =
																				isBookingIntervalWithinOpenHours(
																					openWindowsMs,
																					tsStartMb,
																					tsEndMb
																				);
																			const valid =
																				capOk &&
																				slotOpen &&
																				isCalendarPlacementSlotValid({
																					anchorTableId: tbl._id,
																					party: mb.config.number_of_people,
																					bookingId: mb._id,
																					existingTableIds: mb.table_ids,
																					tables,
																					items,
																					day,
																					tz,
																					startHour,
																					slotMins,
																					durationMins: dur,
																					turnMinutes,
																					tableBlocks,
																				});
																			if (!valid) {
																				return null;
																			}
																			const isRequested =
																				!!placementDetails &&
																				slotMins >=
																					placementDetails.reqMins &&
																				slotMins <
																					placementDetails.reqMins +
																						placementDetails.dur;
																			const isSuggested =
																				!!placementDetails &&
																				!placementDetails.isOriginalSlotFree &&
																				slotMins >=
																					placementDetails.suggestedMins &&
																				slotMins <
																					placementDetails.suggestedMins +
																						placementDetails.dur;
																			return (
																				<PlacementSlot
																					key={`pl-${tbl._id}-${i}`}
																					type="button"
																					aria-label={t(
																						"Place booking",
																						{
																							defaultValue:
																								"Place booking",
																						}
																					)}
																					$valid={valid}
																					$requested={isRequested}
																					$suggested={
																						isSuggested && !isRequested
																					}
																					$hot={
																						dragOverKey ===
																						this.placementHoverKey(tbl._id, slotMins)
																					}
																					style={{
																						left:
																							slotMins * minuteSize,
																						width:
																							slotIntervalMins *
																							minuteSize,
																					}}
																					onClick={(e) => {
																						e.stopPropagation();
																						this.applyPlacement(
																							tbl._id,
																							slotMins,
																							{
																								day,
																								tz,
																								startHour,
																								items,
																								tables,
																							}
																						);
																					}}
																					onMouseEnter={() => {
																						this.setPlacementHover(tbl._id, slotMins);
																					}}
																					onDragOver={(e) => {
																						e.preventDefault();
																						e.dataTransfer.dropEffect = "move";
																						this.setPlacementHover(tbl._id, slotMins);
																					}}
																					onDrop={(e) => {
																						e.preventDefault();
																						e.stopPropagation();
																						this.setState({ dragOverKey: null });
																						const droppedId =
																							e.dataTransfer.getData("text/plain") ||
																							undefined;
																						this.applyPlacement(
																							tbl._id,
																							slotMins,
																							{
																								day,
																								tz,
																								startHour,
																								items,
																								tables,
																							},
																							droppedId
																						);
																					}}
																				/>
																			);
																		}
																	);
																})()}
															{blockTableMode &&
																!placingId &&
																!selectedId &&
																(() => {
																	const dur = turnMinutes;
																	const dayStartBlk = day
																		.clone()
																		.tz(tz)
																		.startOf("day");
																	const numSlotsBlk = Math.max(
																		1,
																		Math.floor(
																			totalMinutes / slotIntervalMins
																		)
																	);
																	return Array.from(
																		{ length: numSlotsBlk },
																		(_, i) => {
																			const slotMins = i * slotIntervalMins;
																			const absStartMinBlk =
																				startHour * 60 + slotMins;
																			const tsStartBlk = dayStartBlk
																				.clone()
																				.add(absStartMinBlk, "minutes")
																				.valueOf();
																			const tsEndBlk =
																				tsStartBlk + dur * 60_000;
																			const slotOpen =
																				isBookingIntervalWithinOpenHours(
																					openWindowsMs,
																					tsStartBlk,
																					tsEndBlk
																				);
																			const valid =
																				slotOpen &&
																				isTableSlotAvailable(
																					tbl._id,
																					slotMins,
																					dur,
																					items,
																					undefined,
																					startHour,
																					tz,
																					durationMinsFn,
																					{ day, tableBlocks }
																				);
																			if (!valid) return null;
																			return (
																				<PlacementSlot
																					key={`blk-${tbl._id}-${i}`}
																					type="button"
																					aria-label={t(
																						"Block table for this slot",
																						{
																							defaultValue:
																								"Block table for this slot",
																						}
																					)}
																					$valid={valid}
																					$hot={
																						dragOverKey ===
																						this.placementHoverKey(
																							tbl._id,
																							slotMins
																						)
																					}
																					style={{
																						left: slotMins * minuteSize,
																						width:
																							slotIntervalMins *
																							minuteSize,
																						background:
																							"rgba(245, 158, 11, 0.18)",
																					}}
																					onClick={(e) => {
																						e.stopPropagation();
																						void this.addTableBlock(
																							tbl._id,
																							slotMins,
																							{
																								day,
																								tz,
																								startHour,
																							}
																						);
																					}}
																					onMouseEnter={() => {
																						this.setPlacementHover(
																							tbl._id,
																							slotMins
																						);
																					}}
																				/>
																			);
																		}
																	);
																})()}
															{!placingId &&
																!selectedId &&
																!blockTableMode &&
																onRequestCreateAtSlot &&
																(() => {
																	const dur = turnMinutes;
																	const capOk =
																		dineInTableCapacity(tbl, 4) >=
																		referencePartyForSlots;
																	const dayStartCr = day
																		.clone()
																		.tz(tz)
																		.startOf("day");
																	const numSlotsCr = Math.max(
																		1,
																		Math.floor(
																			totalMinutes / slotIntervalMins
																		)
																	);
																	return Array.from(
																		{ length: numSlotsCr },
																		(_, i) => {
																			const slotMins = i * slotIntervalMins;
																			const absStartMinCr =
																				startHour * 60 + slotMins;
																			const tsStartCr = dayStartCr
																				.clone()
																				.add(absStartMinCr, "minutes")
																				.valueOf();
																			const tsEndCr =
																				tsStartCr + dur * 60_000;
																			const slotOpen =
																				isBookingIntervalWithinOpenHours(
																					openWindowsMs,
																					tsStartCr,
																					tsEndCr
																				);
																			const valid =
																				capOk &&
																				slotOpen &&
																				isTableSlotAvailable(
																					tbl._id,
																					slotMins,
																					dur,
																					items,
																					undefined,
																					startHour,
																					tz,
																					durationMinsFn,
																					{ day, tableBlocks }
																				);
																			if (!valid) {
																				return null;
																			}
																			return (
																				<CreateTimelineSlot
																					key={`cr-${tbl._id}-${i}`}
																					type="button"
																					aria-label={t(
																						"Create booking here",
																						{
																							defaultValue:
																								"Create booking here",
																						}
																					)}
																					$valid={valid}
																					style={{
																						left:
																							slotMins * minuteSize,
																						width:
																							slotIntervalMins *
																							minuteSize,
																					}}
																					onClick={(e) => {
																						e.stopPropagation();
																						if (this.panMoved) return;
																						this.handleCreateAtSlotClick(
																							slotMins,
																							{
																								day,
																								tz,
																								startHour,
																								tableId: tbl._id,
																							}
																						);
																					}}
																				/>
																			);
																		}
																	);
																})()}
														</TimelineTrack>
													</GridRow>
												);
											})}

											{tables.length === 0 && (
												<GridRow>
													<RowLabel>
														<RowLabelTitle>—</RowLabelTitle>
														<RowLabelMeta>
															{t("Add tables in Dine In settings", {
																defaultValue: "Add tables in Dine In",
															})}
														</RowLabelMeta>
													</RowLabel>
													<TimelineTrack
														$w={timelineInnerWidth}
														$slot={slotSize}
														$minor={minorGrid}
														style={{ minHeight: MIN_TABLE_ROW_PX }}
													/>
												</GridRow>
											)}
										</div>
									</div>
								</ScrollArea>
						</GridWrap>
					</CalendarShell>
				)}
				{adjustmentPending &&
					(() => {
						const adjBooking = items.find(
							(x) => x._id === adjustmentPending.bookingId
						);
						if (!adjBooking) return null;
						return (
							<TimeAdjustBackdrop
								onClick={(e) => {
									if (e.target === e.currentTarget) this.cancelTimeAdjustment();
								}}
							>
								<ResModalCard onClick={(e) => e.stopPropagation()}>
									<ResModalCloseBtn
										type="button"
										aria-label={t("Close", { defaultValue: "Close" })}
										onClick={this.cancelTimeAdjustment}
									>
										&times;
									</ResModalCloseBtn>
									<ResModalHeader>
										<ResModalFeaturedIcon aria-hidden>
											<FaRegClock />
										</ResModalFeaturedIcon>
										<ResModalTitleCol>
											<ResModalTitle>
												{t("Time Adjustment", { defaultValue: "Time Adjustment" })}
											</ResModalTitle>
											<ResModalDesc>
												{t("calendar.timeAdjust.body", {
													defaultValue:
														"Moving {{guest}} to {{when}}. Notify the guest",
													guest: adjBooking.customer.name,
													when: t("datetimeFromTimestamp", {
														value: adjustmentPending.newTs,
													}),
												})}
											</ResModalDesc>
										</ResModalTitleCol>
										<ResModalNumberBadge>
											#{adjBooking.number}
										</ResModalNumberBadge>
									</ResModalHeader>
									<ResModalBody>
										<ResModalActionBtn
											type="button"
											$primary
											$full
											onClick={() => this.finalizeTimeAdjustment(true)}
										>
											{t("Send email notification", { defaultValue: "Send email notification" })}
										</ResModalActionBtn>
										<ResModalActionBtn
											type="button"
											onClick={() => this.finalizeTimeAdjustment(false)}
										>
											{t("Confirm without notification", { defaultValue: "Confirm without notification" })}
										</ResModalActionBtn>
										<ResModalActionBtn
											type="button"
											$full
											onClick={this.cancelTimeAdjustment}
										>
											{t("Cancel and revert", { defaultValue: "Cancel and revert" })}
										</ResModalActionBtn>
									</ResModalBody>
								</ResModalCard>
							</TimeAdjustBackdrop>
						);
					})()}
				{docketBooking && (() => {
					const docketTableId = docketBooking.table_ids?.[0] || null;
					const docketTable = docketTableId ? tables.find((x) => x._id === docketTableId) : null;
					const joinedTableLabel = formatBookingTableLabel(docketBooking.table_ids, tables);
					const tableName =
						joinedTableLabel ||
						docketTable?.name?.trim() ||
						t("Table", { defaultValue: "Table" });
					const dateStr = t("datetimeFromTimestamp", { value: docketBooking.config.timestamp }) || "";
					const paxStr = `${t("number", { value: docketBooking.config.number_of_people }) || docketBooking.config.number_of_people} pax`;
					return (
						<DocketBackdrop
							onClick={(e) => {
								if (e.target === e.currentTarget) this.closeDocket();
							}}
						>
							<ResModalCard onClick={(e) => e.stopPropagation()}>
								<ResModalCloseBtn
									type="button"
									aria-label={t("Close", { defaultValue: "Close" })}
									onClick={this.closeDocket}
								>
									&times;
								</ResModalCloseBtn>
								<ResModalHeader>
									<ResModalFeaturedIcon aria-hidden>
										<FaPrint />
									</ResModalFeaturedIcon>
									<ResModalTitleCol>
										<ResModalTitle>
											{t("Live table docket", { defaultValue: "Live table docket" })}
										</ResModalTitle>
									</ResModalTitleCol>
									<ResModalNumberBadge>
										#{docketBooking.number}
									</ResModalNumberBadge>
								</ResModalHeader>
								<ResModalBody>
									{/* Table row */}
									<ResModalInfoRow>
										<ResModalInfoTitle>
											{docketTableId
												? `${t("Table", { defaultValue: "Table" })} ${tableName}`
												: t("Unassigned", { defaultValue: "Unassigned" })}
										</ResModalInfoTitle>
										<ResModalMetaRow>
											<ResModalMetaLabel>{r.name}</ResModalMetaLabel>
											<ResModalMetaValue>{paxStr}</ResModalMetaValue>
										</ResModalMetaRow>
									</ResModalInfoRow>
									{/* Guest row */}
									<ResModalInfoBlock>
										<ResModalInfoTitle>
											{docketBooking.customer.name}
										</ResModalInfoTitle>
										<ResModalMetaRow>
											<ResModalMetaLabel>{t("Guest", { defaultValue: "Guest" })}</ResModalMetaLabel>
											<ResModalMetaValue>{paxStr}</ResModalMetaValue>
										</ResModalMetaRow>
										<ResModalMetaRow>
											<ResModalMetaLabel>{dateStr}</ResModalMetaLabel>
										</ResModalMetaRow>
									</ResModalInfoBlock>
									{/* Action buttons */}
									<div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "0 12px" }}>
										<ResModalActionBtn
											type="button"
											$primary
											$full
											onClick={this.printDocketWithQR}
										>
											{t("Print docket with table QR", { defaultValue: "Print docket with table QR" })}
										</ResModalActionBtn>
										{r.settings.printers
											.filter((p) => !p.disabled)
											.map((p) => (
												<ResModalActionBtn
													key={p._id}
													type="button"
													$full
													onClick={() => this.printDocket(p._id)}
												>
													{t("Print docket", { defaultValue: "Print docket" })} - {p.name}
												</ResModalActionBtn>
											))}
										<ResModalActionBtn
											type="button"
											$full
											onClick={this.closeDocket}
										>
											{t("Done", { defaultValue: "Done" })}
										</ResModalActionBtn>
									</div>
								</ResModalBody>
							</ResModalCard>
						</DocketBackdrop>
					);
				})()}
			</div>
		);
	}
}

export const BookingsCalendar = withTranslation()(BookingsCalendarClass);
